/**
 * Analytics Provider contracts.
 *
 * AnalyticsProvider   — minimal interface the service holds; stays non-generic so
 *                       Map<string, AnalyticsProvider> avoids variance issues.
 *
 * BaseAnalyticsProvider<TPayload> — abstract class concrete providers extend.
 *   Each provider implements:
 *     transform()  — converts canonical event + properties → provider-specific
 *                    payload, or returns null to skip (event not relevant here)
 *     send()       — delivers the typed payload to the destination
 *   track() is final: it calls transform → send.
 */

import type { AnalyticsEventProperties } from '../types/properties'

export type { AnalyticsEventProperties }

export interface AnalyticsProvider {
  readonly name: string
  track(event: string, properties?: AnalyticsEventProperties): void
  identify(userId: string, traits?: Record<string, unknown>): void
}

export abstract class BaseAnalyticsProvider<TPayload> implements AnalyticsProvider {
  abstract readonly name: string

  abstract transform(event: string, properties: AnalyticsEventProperties): TPayload | null

  abstract send(payload: TPayload): void

  abstract identify(userId: string, traits?: Record<string, unknown>): void

  track(event: string, properties?: AnalyticsEventProperties): void {
    const payload = this.transform(event, properties ?? {})
    if (payload !== null) this.send(payload)
  }
}
